/**
 * Shared SPA SourceCache: memory → local → provider (SWR).
 */

import {
  buildSourceCacheMeta,
  mergeEnvelopeCacheMeta,
  resolveFreshness,
  type ApiEnvelope,
  type SourceCacheMeta,
  type SourceCachePolicy,
  type SourceCacheSource,
} from '@zunelo/shared';
import { createLocalAdapter } from './localAdapter';
import { createMemoryAdapter } from './memoryAdapter';
import type {
  GetOrRevalidateOptions,
  GetOrRevalidateResult,
  SourceCacheAdapter,
  SourceCacheEntry,
} from './types';

export interface SourceCacheOptions {
  memory?: SourceCacheAdapter;
  local?: SourceCacheAdapter;
  /** When false, skip durable local reads/writes even if policy.persistLocal. */
  enableLocal?: boolean;
}

export interface SourceCache {
  getOrRevalidate<T>(options: GetOrRevalidateOptions<T>): GetOrRevalidateResult<T>;
  peek<T>(key: string, source: SourceCacheSource, policy: SourceCachePolicy, nowMs?: number): {
    entry: SourceCacheEntry<T>;
    cache: SourceCacheMeta;
  } | null;
  invalidate(key: string): void;
  writeThrough<T>(
    key: string,
    source: SourceCacheSource,
    policy: SourceCachePolicy,
    envelope: ApiEnvelope<T>,
    layer?: SourceCacheMeta['layer'],
  ): ApiEnvelope<T>;
  clearMemory(): void;
}

function readLayer(
  memory: SourceCacheAdapter,
  local: SourceCacheAdapter | null,
  key: string,
  persistLocal: boolean,
): { entry: SourceCacheEntry<unknown>; layer: SourceCacheMeta['layer'] } | null {
  const mem = memory.get(key);
  if (mem) return { entry: mem, layer: 'memory' };
  if (persistLocal && local) {
    const loc = local.get(key);
    if (loc) return { entry: loc, layer: 'local' };
  }
  return null;
}

export function createSourceCache(options: SourceCacheOptions = {}): SourceCache {
  const memory = options.memory ?? createMemoryAdapter();
  const localEnabled = options.enableLocal !== false;
  const local = localEnabled ? (options.local ?? createLocalAdapter()) : null;

  const writeThrough = <T>(
    key: string,
    source: SourceCacheSource,
    policy: SourceCachePolicy,
    envelope: ApiEnvelope<T>,
    layer: SourceCacheMeta['layer'] = 'provider',
  ): ApiEnvelope<T> => {
    const cache =
      envelope.cache ??
      buildSourceCacheMeta({
        source,
        layer,
        policy,
        fetchedAt: envelope.serverTime,
      });
    const withMeta = mergeEnvelopeCacheMeta(envelope, { ...cache, layer, source });
    const entry: SourceCacheEntry<T> = {
      data: withMeta.data,
      cache: withMeta.cache!,
      serverTime: withMeta.serverTime,
    };
    memory.set(key, entry);
    if (policy.persistLocal && local) {
      local.set(key, entry);
    }
    return withMeta;
  };

  const peek = <T>(
    key: string,
    source: SourceCacheSource,
    policy: SourceCachePolicy,
    nowMs: number = Date.now(),
  ): { entry: SourceCacheEntry<T>; cache: SourceCacheMeta } | null => {
    const hit = readLayer(memory, local, key, policy.persistLocal);
    if (!hit) return null;
    const freshness = resolveFreshness(hit.entry.cache, policy, nowMs);
    if (freshness === 'miss') return null;
    const cache: SourceCacheMeta = {
      ...hit.entry.cache,
      freshness,
      layer: hit.layer,
      source,
    };
    return { entry: hit.entry as SourceCacheEntry<T>, cache };
  };

  const inFlight = new Map<string, { promise: Promise<ApiEnvelope<unknown>>; force: boolean }>();
  const inFlightTokens = new Map<string, symbol>();

  const invalidate = (key: string): void => {
    inFlight.delete(key);
    inFlightTokens.delete(key);
    memory.delete(key);
    local?.delete(key);
  };

  const getOrRevalidate = <T>(
    optionsInner: GetOrRevalidateOptions<T>,
  ): GetOrRevalidateResult<T> => {
    const nowMs = optionsInner.nowMs ?? Date.now();
    const { key, source, policy, fetcher, force } = optionsInner;
    const isForce = force === true;
    const peeked = isForce ? null : peek<T>(key, source, policy, nowMs);

    const runFetch = (): Promise<ApiEnvelope<T>> => {
      const existing = inFlight.get(key);
      if (existing && (!isForce || existing.force)) {
        return existing.promise as Promise<ApiEnvelope<T>>;
      }

      const token = Symbol('fetch');
      const fetchPromise: Promise<ApiEnvelope<T>> = (async () => {
        try {
          const envelope = await fetcher();
          return writeThrough(key, source, policy, envelope, envelope.cache?.layer ?? 'provider');
        } finally {
          if (inFlightTokens.get(key) === token) {
            inFlightTokens.delete(key);
            inFlight.delete(key);
          }
        }
      })();

      inFlightTokens.set(key, token);
      inFlight.set(key, { promise: fetchPromise as Promise<ApiEnvelope<unknown>>, force: isForce });
      return fetchPromise;
    };

    if (!peeked) {
      const revalidatePromise = runFetch();
      return {
        data: undefined,
        envelope: undefined,
        cache: undefined,
        revalidatePromise,
        fromCache: false,
      };
    }

    const cachedEnvelope: ApiEnvelope<T> = {
      data: peeked.entry.data,
      serverTime: peeked.entry.serverTime ?? peeked.cache.fetchedAt,
      cache: peeked.cache,
    };

    const needsRevalidate = isForce || peeked.cache.freshness !== 'fresh';
    const revalidatePromise = needsRevalidate
      ? runFetch()
      : Promise.resolve(cachedEnvelope);

    // Promote local hit into memory for subsequent peeks.
    memory.set(key, { ...peeked.entry, cache: peeked.cache });

    return {
      data: peeked.entry.data,
      envelope: cachedEnvelope,
      cache: peeked.cache,
      revalidatePromise,
      fromCache: true,
    };
  };

  return {
    getOrRevalidate,
    peek,
    invalidate,
    writeThrough,
    clearMemory: () => memory.clear(),
  };
}

/** Process-wide default SPA source cache. */
let defaultSourceCache: SourceCache | null = null;

export function getDefaultSourceCache(): SourceCache {
  if (!defaultSourceCache) {
    defaultSourceCache = createSourceCache();
  }
  return defaultSourceCache;
}

export function resetDefaultSourceCacheForTests(cache?: SourceCache | null): void {
  defaultSourceCache = cache === undefined ? null : cache;
}
